import { z } from 'zod'
import { ValueObject } from '@lukso/controllerapps-sharedlogic-ddd-core'

import { Permissions } from './Permissions'
import { ControllerAddress } from './ControllerAddress'
import { DeterminedControllerName } from './controller/DeterminedControllerName'
import { CustomControllerName } from './controller/CustomControllerName'
import { ControllerName } from './controller/ControllerName'

const ControllerPropsSchema = z.object({
  address: z.instanceof(ControllerAddress),
  permissions: z.instanceof(Permissions),
  /**
   * The name of the controller as we determine it from our heuristics
   */
  determinedName: z.instanceof(DeterminedControllerName).optional(),
  /**
   * The name of the controller as the user has set it
   */
  customName: z.instanceof(CustomControllerName).optional(),
})

type ControllerProps = z.infer<typeof ControllerPropsSchema>

export class Controller extends ValueObject<ControllerProps> {
  public getAddress(): ControllerAddress {
    return this.props.address
  }

  public getPermissions(): Permissions {
    return this.props.permissions
  }

  /**
   * @returns The name of the controller as we determine it from our heuristics
   */
  public getDeterminedName(): DeterminedControllerName | undefined {
    return this.props.determinedName
  }

  /**
   * @returns The name of the controller as the user has set it
   */
  public getCustomName(): CustomControllerName | undefined {
    return this.props.customName
  }

  /**
   * @returns The custom name of the controller if it's set, otherwise the determined name if it's set
   */
  public getName(): ControllerName | undefined {
    const { customName, determinedName } = this.props
    const controllerNameValue =
      customName?.getValue() ?? determinedName?.getValue()
    return controllerNameValue
      ? ControllerName.create({ controllerNameValue })
      : undefined
  }

  public static create(props: ControllerProps): Controller {
    const validatedProps = ControllerPropsSchema.parse(props)
    return new Controller(validatedProps)
  }
}
